import { IFilterItem } from './types';

export default class FilterSet<T> {
  items: IFilterItem<T>[];

  constructor(items: IFilterItem<T>[]) {
    this.items = items;
  }

  add(item: IFilterItem<T>) {
    this.items.push(item);
  }

  getImmutableSet() {
    return new FilterSet(this.items);
  }

  mergeList(nextSet: FilterSet<T>) {
    const result = this.getImmutableSet();
    nextSet.items.forEach(item => !result.has(item.value) && result.add(item));
    return result;
  }

  mergeViaCountList(nextSet: FilterSet<T>) {
    const result = this.getImmutableSet();
    nextSet.items.forEach((item, i) => {
      const prevLabel = result.items[i].subLabel;
      const nextLabel = item.subLabel;
      if (prevLabel) {
        result.items[i].subLabel = nextLabel
          ? Math.min(Number(prevLabel), Number(nextLabel)).toString()
          : prevLabel;
      } else {
        result.items[i].subLabel = nextLabel;
      }
    });
    return result;
  }

  has(value: T) {
    return this.items.find(el => el.enable && el.value === value);
  }

  hasSelected(value: T) {
    return this.items.find(
      el => el.enable && el.selected && el.value === value,
    );
  }

  select(value: T) {
    const found = this.items.find(el => el.enable && el.value === value);
    if (!found) return;
    found.selected = true;
  }

  toggle(value: T) {
    const found = this.items.find(el => el.enable && el.value === value);
    if (!found) return;
    found.selected = !found.selected;
  }

  reset() {
    this.items = this.items.map(el => ({
      ...el,
      selected: false,
    }));
  }

  getSize() {
    return this.items.length;
  }

  getValueSet() {
    return new Set<T>(this.items.map(el => el.value));
  }

  getSelectedValues() {
    return this.items.filter(el => el.selected).map(el => el.value);
  }

  getSelectedSet() {
    return new Set<T>(this.getSelectedValues());
  }

  isActive() {
    return this.getSelectedValues().length > 0;
  }

  filter(
    callbackFn: (
      element: IFilterItem<T>,
      index: number,
      array: IFilterItem<T>[],
    ) => boolean,
  ) {
    return new FilterSet(this.items.filter(callbackFn));
  }
}
